import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({ baseURL: `${BASE}/api` });

const TOKEN_KEY = "hrflo_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Auth
export const authApi = {
  signup: (payload) => api.post("/auth/signup", payload).then((r) => r.data),
  login: (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

// Employees
export const employeesApi = {
  list: () => api.get("/employees").then((r) => r.data),
  create: (data) => api.post("/employees", data).then((r) => r.data),
  update: (id, data) => api.put(`/employees/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/employees/${id}`).then((r) => r.data),
};

export const dashboardApi = {
  get: () => api.get("/dashboard").then((r) => r.data),
};

export const leadsApi = {
  create: (payload) => api.post("/leads", payload).then((r) => r.data),
};

export const leaveApi = {
  list: () => api.get("/leave").then((r) => r.data),
  create: (data) => api.post("/leave", data).then((r) => r.data),
  decide: (id, status, note = "") => api.post(`/leave/${id}/decision`, { status, note }).then((r) => r.data),
  balances: () => api.get("/leave/balances").then((r) => r.data),
};

export const attendanceApi = {
  list: (date) => api.get(`/attendance?date=${date}`).then((r) => r.data),
  upsert: (data) => api.post("/attendance", data).then((r) => r.data),
  summary: (date) => api.get(`/attendance/summary?date=${date}`).then((r) => r.data),
};

// ---- Employee portal (separate token) ----
const PORTAL_KEY = "hrflo_portal_token";
export function getPortalToken() { return localStorage.getItem(PORTAL_KEY); }
export function setPortalToken(t) { if (t) localStorage.setItem(PORTAL_KEY, t); else localStorage.removeItem(PORTAL_KEY); }
const portal = axios.create({ baseURL: `${BASE}/api` });
portal.interceptors.request.use((config) => {
  const t = getPortalToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
export const portalApi = {
  org: (code) => portal.get(`/portal/${code}/org`).then((r) => r.data),
  login: (code, employee_number, surname) => portal.post(`/portal/${code}/login`, { employee_number, surname }).then((r) => r.data),
  me: () => portal.get("/portal/me").then((r) => r.data),
  listLeave: () => portal.get("/portal/leave").then((r) => r.data),
  createLeave: (data) => portal.post("/portal/leave", data).then((r) => r.data),
  clock: () => portal.post("/portal/clock").then((r) => r.data),
  today: () => portal.get("/portal/attendance/today").then((r) => r.data),
};

// ---- HRFlo Time admin (org token) ----
export const timeApi = {
  biometrics: () => api.get("/time/biometrics").then((r) => r.data),
  enroll: (employee_id, descriptor) => api.post("/time/enroll", { employee_id, descriptor }).then((r) => r.data),
  biometricAction: (employee_id, action) => api.post(`/time/biometric/${employee_id}/${action}`).then((r) => r.data),
  setPin: (employee_id, pin) => api.post("/time/pin", { employee_id, pin }).then((r) => r.data),
  devices: () => api.get("/time/devices").then((r) => r.data),
  createDevice: (data) => api.post("/time/devices", data).then((r) => r.data),
  deviceStatus: (id, status) => api.post(`/time/devices/${id}/status`, { status }).then((r) => r.data),
  deleteDevice: (id) => api.delete(`/time/devices/${id}`).then((r) => r.data),
  events: (date) => api.get(`/time/events?date=${date}`).then((r) => r.data),
  summary: (date) => api.get(`/time/summary?date=${date}`).then((r) => r.data),
  exceptions: (date) => api.get(`/time/exceptions?date=${date}`).then((r) => r.data),
};

// ---- HRFlo Time kiosk (separate device token) ----
const KIOSK_KEY = "hrflo_kiosk_token";
export function getKioskToken() { return localStorage.getItem(KIOSK_KEY); }
export function setKioskToken(t) { if (t) localStorage.setItem(KIOSK_KEY, t); else localStorage.removeItem(KIOSK_KEY); }
const kioskClient = axios.create({ baseURL: `${BASE}/api` });
kioskClient.interceptors.request.use((config) => {
  const t = getKioskToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});
export const kioskApi = {
  login: (org_code, username, password) => kioskClient.post("/time/kiosk/login", { org_code, username, password }).then((r) => r.data),
  me: () => kioskClient.get("/time/kiosk/me").then((r) => r.data),
  identify: (descriptor) => kioskClient.post("/time/kiosk/identify", { descriptor }).then((r) => r.data),
  pin: (pin) => kioskClient.post("/time/kiosk/pin", { pin }).then((r) => r.data),
};

// Super-admin
export const adminApi = {
  stats: () => api.get("/admin/stats").then((r) => r.data),
  orgs: () => api.get("/admin/organizations").then((r) => r.data),
  orgDetail: (id) => api.get(`/admin/organizations/${id}`).then((r) => r.data),
  setStatus: (id, status) => api.post(`/admin/organizations/${id}/status`, { status }).then((r) => r.data),
  approve: (id) => api.post(`/admin/organizations/${id}/approve`).then((r) => r.data),
  extend: (id, days = 15) => api.post(`/admin/organizations/${id}/extend`, { days }).then((r) => r.data),
  users: () => api.get("/admin/users").then((r) => r.data),
  audit: () => api.get("/admin/audit").then((r) => r.data),
  leads: () => api.get("/admin/leads").then((r) => r.data),
  leadStatus: (id, status) => api.post(`/admin/leads/${id}/status`, { status }).then((r) => r.data),
};
